/**
 * src/pages/Signals.tsx
 *
 * Signals tab — Tony's 2026-05-10 ask to surface Hot/Meh/Weak feedback
 * data for the prospective market intel analyst. Read-only analytics over
 * the `events` collection (deal_feedback type), with breakdowns by
 * category × source × modelKey × dealTier.
 *
 * Sections:
 *   A. Headline KPIs (Hot/Meh/Weak counts, response rate, ratio, distinct users)
 *   B. Stacked time-series (Hot/Meh/Weak per day, deliveries overlay)
 *   C. Category breakdown
 *   D. Source breakdown
 *   E. dealTier breakdown
 *   F. Top modelKeys by Hot
 *   G. Top modelKeys by Weak
 *   H. Per-user concentration
 *   I. Hot:Weak ratio per modelKey
 *   J. Click-through pairing
 *   K. Time-of-day distribution
 *   L. Filter bar (category / source / dealTier / modelKey / time-window)
 *   M. CSV export per section
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import {
  getSignalsHeadline,
  getSignalsTimeseries,
  getSignalsBreakdown,
  getTopModelKeys,
  getUserConcentration,
  getHotWeakRatio,
  getClickPairing,
  getTimeOfDay,
  signalsExportPath,
  type SignalFiltersInput,
  type SignalsTimeSeriesBucket,
  type SignalsBreakdownRow,
  type TopModelKeyRow,
  type UserConcentrationRow,
  type HotWeakRatioRow,
  type ClickPairingRow,
  type TimeOfDayBucket,
} from '@/api/signals';
import ChartCard from '@/components/ChartCard';
import TimeWindowToggle from '@/components/TimeWindowToggle';
import CsvExportButton from '@/components/CsvExportButton';
import { DEAL_TIER_EMOJI, type DealTier } from '@/lib/dealTier';
import { cn } from '@/lib/cn';
import { txt } from '@/lib/format';

const DAY = 24 * 60 * 60 * 1000;

export default function Signals() {
  const [sinceMs, setSinceMs] = useState<number>(30 * DAY);
  const [filters, setFilters] = useState<SignalFiltersInput>({});

  const query = useMemo(() => ({ sinceMs, ...filters }), [sinceMs, filters]);

  return (
    <div className="space-y-4 max-w-7xl">
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-semibold">User Signals</h1>
          <p className="text-sm text-slate-500 mt-1">
            🔥 Hot · 😐 Meh · 👎 Weak feedback breakdowns by category, source, modelKey, dealTier
          </p>
        </div>
        <TimeWindowToggle value={sinceMs} onChange={setSinceMs} label="window" />
      </header>

      <FilterBar filters={filters} setFilters={setFilters} />
      <HeadlineRow q={query} />

      <ChartCard
        title="Hot / Meh / Weak — daily"
        subtitle="Stacked area per day · deliveries overlay (light line)"
        actions={<CsvExportButton path={signalsExportPath({ section: 'timeseries', ...query })} filename="signals_timeseries.csv" />}
      >
        <TimeSeriesPanel q={query} />
      </ChartCard>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <BreakdownCard dim="category" q={query} />
        <BreakdownCard dim="source"   q={query} />
        <BreakdownCard dim="dealTier" q={query} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <TopModelKeysCard kind="hot"  q={query} />
        <TopModelKeysCard kind="weak" q={query} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <RatioCard q={query} />
        <ClickPairingCard q={query} />
      </div>

      <UserConcentrationCard q={query} />
      <TimeOfDayCard q={query} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Filter bar
// ─────────────────────────────────────────────────────────────────────────────

function FilterBar({ filters, setFilters }: { filters: SignalFiltersInput; setFilters: (f: SignalFiltersInput) => void }) {
  return (
    <div className="flex flex-wrap items-end gap-2 bg-slate-900/40 border border-slate-800 rounded-md p-3">
      {(['category', 'source', 'modelKey'] as const).map(k => (
        <label key={k} className="text-xs text-slate-400 flex flex-col gap-1">
          {k}
          <input
            value={filters[k] ?? ''}
            onChange={e => setFilters({ ...filters, [k]: e.target.value || undefined })}
            placeholder={k === 'category' ? 'phones' : k === 'source' ? 'cex_uk' : 'iphone_15'}
            className="bg-slate-900 border border-slate-800 rounded px-2 py-1 text-sm w-32 font-mono"
          />
        </label>
      ))}
      <label className="text-xs text-slate-400 flex flex-col gap-1">
        dealTier
        <select
          value={filters.dealTier ?? ''}
          onChange={e => setFilters({ ...filters, dealTier: (e.target.value || undefined) as DealTier })}
          className="bg-slate-900 border border-slate-800 rounded px-2 py-1 text-sm"
        >
          <option value="">any</option>
          <option value="exceptional">🔥 exceptional</option>
          <option value="strong">✓ strong</option>
          <option value="standard">• standard</option>
        </select>
      </label>
      <label className="text-xs text-slate-400 flex flex-col gap-1">
        kind
        <select
          value={filters.kind ?? ''}
          onChange={e => setFilters({ ...filters, kind: (e.target.value || undefined) as any })}
          className="bg-slate-900 border border-slate-800 rounded px-2 py-1 text-sm"
        >
          <option value="">all</option>
          <option value="hot">hot</option>
          <option value="meh">meh</option>
          <option value="weak">weak</option>
        </select>
      </label>
      <button
        onClick={() => setFilters({})}
        className="text-xs text-slate-400 hover:text-white px-2 py-1 self-end"
      >
        reset
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// A. Headline KPIs
// ─────────────────────────────────────────────────────────────────────────────

function HeadlineRow({ q }: { q: any }) {
  const { data, isLoading } = useQuery({
    queryKey: ['signals', 'headline', q],
    queryFn:  () => getSignalsHeadline(q),
  });
  const Kpi = ({ label, value, color }: { label: string; value: string | number | null; color?: string }) => (
    <div className="rounded border border-slate-800 bg-slate-900/30 px-3 py-2 min-w-0">
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={cn('text-base font-mono mt-0.5 truncate', color)}>
        {isLoading ? <Loader2 className="w-3 h-3 animate-spin inline" /> : (value ?? '—')}
      </div>
    </div>
  );
  return (
    <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
      <Kpi label="🔥 hot"  value={data?.hotCount?.toLocaleString() ?? null} color="text-rose-300" />
      <Kpi label="😐 meh"  value={data?.mehCount?.toLocaleString() ?? null} color="text-amber-300" />
      <Kpi label="👎 weak" value={data?.weakCount?.toLocaleString() ?? null} color="text-slate-400" />
      <Kpi label="response %" value={data?.responseRate !== null && data?.responseRate !== undefined ? `${data.responseRate}%` : null} />
      <Kpi label="hot/(hot+weak) %" value={data?.hotToWeakRatio !== null && data?.hotToWeakRatio !== undefined ? `${data.hotToWeakRatio}%` : null} />
      <Kpi label="distinct users" value={data?.distinctUsers?.toLocaleString() ?? null} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// B. Time series
// ─────────────────────────────────────────────────────────────────────────────

function TimeSeriesPanel({ q }: { q: any }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['signals', 'timeseries', q],
    queryFn:  () => getSignalsTimeseries({ ...q, bucket: 'day' }),
  });
  if (isLoading) return <Loader2 className="w-3 h-3 animate-spin" />;
  if (isError)   return <span className="text-rose-400 text-xs">Failed to load.</span>;
  const rows = data?.rows ?? [];
  if (!rows.length) return <span className="text-xs text-slate-500">No signal data in window.</span>;
  return <TimeSeriesChart rows={rows} />;
}

function TimeSeriesChart({ rows }: { rows: SignalsTimeSeriesBucket[] }) {
  const max = Math.max(1, ...rows.map(r => r.hot + r.meh + r.weak));
  const maxDel = Math.max(1, ...rows.map(r => r.deliveries));
  const w = 100, h = 130;
  const stepX = w / Math.max(1, rows.length - 1);
  // Stacked area construction — top, mid, bottom polygons
  const hotPts: string[] = [];
  const mehPts: string[] = [];
  const weakPts: string[] = [];
  const delPts: string[] = [];
  rows.forEach((r, i) => {
    const x = i * stepX;
    const total = r.hot + r.meh + r.weak;
    // Stack from bottom: hot first (bottom), then meh, then weak (top)
    const yHot = h - (r.hot / max) * (h - 4);
    const yMeh = yHot - (r.meh / max) * (h - 4);
    const yWeak = yMeh - (r.weak / max) * (h - 4);
    hotPts.push(`${x.toFixed(2)},${yHot.toFixed(2)}`);
    mehPts.push(`${x.toFixed(2)},${yMeh.toFixed(2)}`);
    weakPts.push(`${x.toFixed(2)},${yWeak.toFixed(2)}`);
    delPts.push(`${x.toFixed(2)},${(h - (r.deliveries / maxDel) * (h - 4)).toFixed(2)}`);
    void total;
  });

  const area = (pts: string[], baseY: number, color: string) => {
    if (!pts.length) return null;
    const first = pts[0].split(',')[0];
    const last  = pts[pts.length - 1].split(',')[0];
    return (
      <path d={`M ${pts.join(' L ')} L ${last},${baseY} L ${first},${baseY} Z`} fill={color} fillOpacity={0.7} />
    );
  };

  return (
    <div className="space-y-2">
      <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="w-full" style={{ height: h }}>
        {area(hotPts,  h,      '#fb7185')}
        {area(mehPts,  h,      '#f59e0b')}
        {area(weakPts, h,      '#64748b')}
        <polyline points={delPts.join(' ')} fill="none" stroke="#94a3b8" strokeWidth={0.6} strokeDasharray="2 2" />
      </svg>
      <div className="flex gap-3 text-[11px] text-slate-400 flex-wrap">
        <Legend color="#fb7185" label="🔥 hot" />
        <Legend color="#f59e0b" label="😐 meh" />
        <Legend color="#64748b" label="👎 weak" />
        <Legend color="#94a3b8" label="deliveries (overlay)" dashed />
      </div>
      <div className="flex justify-between text-[10px] text-slate-500 font-mono">
        <span>{rows[0]?.ts.slice(0, 10)}</span>
        <span>{rows[rows.length - 1]?.ts.slice(0, 10)}</span>
      </div>
    </div>
  );
}

function Legend({ color, label, dashed }: { color: string; label: string; dashed?: boolean }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span
        className="inline-block w-3 h-1 align-middle"
        style={{ background: color, borderStyle: dashed ? 'dashed' : undefined }}
      />
      {label}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// C/D/E. Breakdown by category / source / dealTier
// ─────────────────────────────────────────────────────────────────────────────

function BreakdownCard({ dim, q }: { dim: 'category' | 'source' | 'dealTier'; q: any }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['signals', 'breakdown', dim, q],
    queryFn:  () => getSignalsBreakdown({ dim, ...q }),
  });
  const rows = data?.rows ?? [];
  const title = dim === 'category' ? 'By category' : dim === 'source' ? 'By source' : 'By dealTier';
  return (
    <ChartCard
      title={title}
      subtitle={dim === 'dealTier' ? '🔥 / ✓ / • split' : undefined}
      isLoading={isLoading} isError={isError} isEmpty={rows.length === 0}
      actions={<CsvExportButton path={signalsExportPath({ section: 'breakdown', dim, ...q })} filename={`signals_${dim}.csv`} />}
    >
      <BreakdownTable rows={rows} dim={dim} />
    </ChartCard>
  );
}

function BreakdownTable({ rows, dim }: { rows: SignalsBreakdownRow[]; dim: 'category' | 'source' | 'dealTier' }) {
  const max = Math.max(1, ...rows.map(r => r.total));
  const labelFor = (k: string) => {
    if (dim !== 'dealTier') return k;
    const tier = k as DealTier;
    return `${DEAL_TIER_EMOJI[tier] ?? ''} ${tier}`;
  };
  return (
    <div className="space-y-1">
      {rows.map(r => (
        <div key={r.key} className="text-xs">
          <div className="flex justify-between mb-0.5">
            <span className="font-mono text-slate-200">{labelFor(r.key)}</span>
            <span className="tabular-nums text-slate-400">{r.total}</span>
          </div>
          <div className="flex h-2 rounded overflow-hidden bg-slate-900">
            <div style={{ width: `${(r.hot  / max) * 100}%` }} className="bg-rose-400" title={`hot: ${r.hot}`} />
            <div style={{ width: `${(r.meh  / max) * 100}%` }} className="bg-amber-400" title={`meh: ${r.meh}`} />
            <div style={{ width: `${(r.weak / max) * 100}%` }} className="bg-slate-500" title={`weak: ${r.weak}`} />
          </div>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// F/G. Top modelKeys by Hot / Weak
// ─────────────────────────────────────────────────────────────────────────────

function TopModelKeysCard({ kind, q }: { kind: 'hot' | 'weak'; q: any }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['signals', 'top-modelkeys', kind, q],
    queryFn:  () => getTopModelKeys({ kind, ...q, limit: 25 }),
  });
  const rows = data?.rows ?? [];
  const emoji = kind === 'hot' ? '🔥' : '👎';
  const sectionKey = kind === 'hot' ? 'top-hot' : 'top-weak';
  return (
    <ChartCard
      title={`Top modelKeys by ${emoji} ${kind}`}
      isLoading={isLoading} isError={isError} isEmpty={rows.length === 0}
      actions={<CsvExportButton path={signalsExportPath({ section: sectionKey, ...q })} filename={`signals_top_${kind}.csv`} />}
    >
      <TopModelKeysTable rows={rows} />
    </ChartCard>
  );
}

function TopModelKeysTable({ rows }: { rows: TopModelKeyRow[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="text-slate-500">
          <tr>
            <th className="text-left  px-2 py-1">modelKey</th>
            <th className="text-right px-2 py-1">🔥</th>
            <th className="text-right px-2 py-1">😐</th>
            <th className="text-right px-2 py-1">👎</th>
            <th className="text-right px-2 py-1">ratio</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.modelKey} className="border-t border-slate-800">
              <td className="px-2 py-1 font-mono text-slate-200">{txt(r.modelKey)}</td>
              <td className="px-2 py-1 text-right tabular-nums text-rose-300">{r.hot}</td>
              <td className="px-2 py-1 text-right tabular-nums text-amber-300">{r.meh}</td>
              <td className="px-2 py-1 text-right tabular-nums text-slate-400">{r.weak}</td>
              <td className="px-2 py-1 text-right tabular-nums text-slate-400 font-mono">
                {r.hotWeakRatio !== null ? `${r.hotWeakRatio}%` : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// I. Hot:Weak ratio per modelKey
// ─────────────────────────────────────────────────────────────────────────────

function RatioCard({ q }: { q: any }) {
  const [minVol, setMinVol] = useState(10);
  const { data, isLoading, isError } = useQuery({
    queryKey: ['signals', 'ratio', q, minVol],
    queryFn:  () => getHotWeakRatio({ ...q, minVolume: minVol, limit: 25 }),
  });
  const rows = data?.rows ?? [];
  return (
    <ChartCard
      title="Hot:Weak ratio per modelKey"
      subtitle={`hot / (hot + weak) · min volume ≥ ${minVol}`}
      isLoading={isLoading} isError={isError} isEmpty={rows.length === 0}
      actions={
        <div className="flex items-center gap-2">
          <label className="text-[10px] text-slate-500">min vol</label>
          <input
            type="number" value={minVol}
            onChange={e => setMinVol(Math.max(1, Number(e.target.value) || 1))}
            className="w-14 bg-slate-900 border border-slate-800 rounded text-xs px-1.5 py-0.5"
          />
          <CsvExportButton path={signalsExportPath({ section: 'ratio', ...q })} filename="signals_ratio.csv" />
        </div>
      }
    >
      <RatioTable rows={rows} />
    </ChartCard>
  );
}

function RatioTable({ rows }: { rows: HotWeakRatioRow[] }) {
  return (
    <table className="w-full text-xs">
      <thead className="text-slate-500">
        <tr>
          <th className="text-left px-2 py-1">modelKey</th>
          <th className="text-right px-2 py-1">🔥</th>
          <th className="text-right px-2 py-1">👎</th>
          <th className="text-right px-2 py-1">vol</th>
          <th className="text-right px-2 py-1">ratio</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(r => (
          <tr key={r.modelKey} className="border-t border-slate-800">
            <td className="px-2 py-1 font-mono text-slate-200">{txt(r.modelKey)}</td>
            <td className="px-2 py-1 text-right tabular-nums text-rose-300">{r.hot}</td>
            <td className="px-2 py-1 text-right tabular-nums text-slate-400">{r.weak}</td>
            <td className="px-2 py-1 text-right tabular-nums text-slate-500">{r.volume}</td>
            <td className={cn(
              'px-2 py-1 text-right tabular-nums font-mono',
              r.ratio >= 70 ? 'text-emerald-400' : r.ratio <= 30 ? 'text-rose-400' : 'text-amber-300',
            )}>
              {r.ratio}%
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// J. Click pairing
// ─────────────────────────────────────────────────────────────────────────────

function ClickPairingCard({ q }: { q: any }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['signals', 'click-pairing', q],
    queryFn:  () => getClickPairing({ ...q, limit: 25 }),
  });
  const rows = data?.rows ?? [];
  return (
    <ChartCard
      title="Hot ↔ Click pairing"
      subtitle="Fraction of Hot taps with a matching deal_clicked event"
      isLoading={isLoading} isError={isError} isEmpty={rows.length === 0}
    >
      <ClickPairingTable rows={rows} />
    </ChartCard>
  );
}

function ClickPairingTable({ rows }: { rows: ClickPairingRow[] }) {
  return (
    <table className="w-full text-xs">
      <thead className="text-slate-500">
        <tr>
          <th className="text-left  px-2 py-1">modelKey</th>
          <th className="text-right px-2 py-1">🔥 taps</th>
          <th className="text-right px-2 py-1">+ click</th>
          <th className="text-right px-2 py-1">ratio</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(r => (
          <tr key={r.modelKey} className="border-t border-slate-800">
            <td className="px-2 py-1 font-mono text-slate-200">{txt(r.modelKey)}</td>
            <td className="px-2 py-1 text-right tabular-nums text-rose-300">{r.hotCount}</td>
            <td className="px-2 py-1 text-right tabular-nums text-emerald-300">{r.hotWithClick}</td>
            <td className="px-2 py-1 text-right tabular-nums font-mono">{r.ratio}%</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// H. Per-user concentration
// ─────────────────────────────────────────────────────────────────────────────

function UserConcentrationCard({ q }: { q: any }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['signals', 'user-concentration', q],
    queryFn:  () => getUserConcentration({ ...q, limit: 50 }),
  });
  const rows = data?.rows ?? [];
  return (
    <ChartCard
      title="Per-user concentration"
      subtitle="Who's giving the most signals · click tgId → Users tab"
      isLoading={isLoading} isError={isError} isEmpty={rows.length === 0}
      actions={<CsvExportButton path={signalsExportPath({ section: 'user-concentration', ...q })} filename="signals_users.csv" />}
    >
      <UserConcentrationTable rows={rows} />
    </ChartCard>
  );
}

function UserConcentrationTable({ rows }: { rows: UserConcentrationRow[] }) {
  return (
    <table className="w-full text-xs">
      <thead className="text-slate-500">
        <tr>
          <th className="text-left  px-2 py-1">tgId</th>
          <th className="text-right px-2 py-1">total</th>
          <th className="text-right px-2 py-1">🔥</th>
          <th className="text-right px-2 py-1">😐</th>
          <th className="text-right px-2 py-1">👎</th>
          <th className="text-right px-2 py-1">last</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(r => (
          <tr key={r.tgId} className="border-t border-slate-800">
            <td className="px-2 py-1 font-mono text-slate-200">{r.tgId}</td>
            <td className="px-2 py-1 text-right tabular-nums text-slate-300">{r.total}</td>
            <td className="px-2 py-1 text-right tabular-nums text-rose-300">{r.hot}</td>
            <td className="px-2 py-1 text-right tabular-nums text-amber-300">{r.meh}</td>
            <td className="px-2 py-1 text-right tabular-nums text-slate-400">{r.weak}</td>
            <td className="px-2 py-1 text-right tabular-nums text-slate-500">
              {r.lastEventAt ? r.lastEventAt.slice(5, 16).replace('T', ' ') : '—'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// K. Time-of-day distribution
// ─────────────────────────────────────────────────────────────────────────────

function TimeOfDayCard({ q }: { q: any }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['signals', 'time-of-day', q],
    queryFn:  () => getTimeOfDay(q),
  });
  return (
    <ChartCard
      title="Time of day (Europe/London)"
      subtitle="Quiet-hours band (22:00–08:00) overlaid · helps spot when feedback happens"
      isLoading={isLoading} isError={isError} isEmpty={!data?.rows?.length}
    >
      {data && <TimeOfDayChart rows={data.rows} />}
    </ChartCard>
  );
}

function TimeOfDayChart({ rows }: { rows: TimeOfDayBucket[] }) {
  const max = Math.max(1, ...rows.map(r => r.total));
  const w = 100;
  const h = 100;
  const barW = w / 24;
  return (
    <div>
      <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="w-full" style={{ height: h }}>
        {/* Quiet-hours bands: 22-24 and 0-8 */}
        <rect x={22 * barW} y={0} width={2 * barW} height={h} fill="#6366f1" fillOpacity={0.1} />
        <rect x={0}          y={0} width={8 * barW} height={h} fill="#6366f1" fillOpacity={0.1} />
        {rows.map(r => {
          const barH = (r.total / max) * (h - 2);
          return (
            <rect key={r.hour}
              x={r.hour * barW + barW * 0.1}
              y={h - barH - 1}
              width={barW * 0.8}
              height={barH}
              fill="#fb7185"
              fillOpacity={0.7}
            >
              <title>{`${r.hour}:00 — total ${r.total} (🔥${r.hot} 😐${r.meh} 👎${r.weak})`}</title>
            </rect>
          );
        })}
      </svg>
      <div className="flex justify-between text-[10px] text-slate-500 mt-1 font-mono">
        <span>00</span><span>06</span><span>12</span><span>18</span><span>23</span>
      </div>
    </div>
  );
}
